/**
 * Matriz RACI Rev.1 (Out/2026) — GUPM-192, fatia RACI da aba Outros. TS puro.
 *
 * Fonte: documento de trabalho JF Brasil "Matriz RACI — CMG e Atividades
 * Correlatas" (GeoGuard UPM — RACI, Rev. 1, Outubro/2026), seção 02. Status do
 * documento: RASCUNHO, aguardando aprovação — a tela mostra isso em destaque.
 *
 * SEM PII: só partes (papéis/empresas) e atividades. Os pontos focais (seção 01
 * do PDF: nomes, telefones, e-mails) NÃO entram no código — vêm da tabela
 * `contacts` (aba Contatos), que é onde a Rev.1 foi carregada (GUPM-202).
 *
 * Gate de perfil (`podeVerMatrizRaci`) é de EXIBIÇÃO: o conteúdo é estático no
 * bundle. Se um dia o operador não puder ler nem pelo bundle, a matriz tem de ir
 * para uma tabela com RLS.
 */

export type RaciLetra = 'R' | 'A' | 'C' | 'I';

export type ParteRaciId =
  | 'EoR' | 'JF' | 'RT' | 'LID' | 'PAEBM' | 'EOB' | 'PROJ' | 'SMB' | 'TTECH' | 'TECAL' | 'TI';

export interface ParteRaci {
  /** Sigla da coluna, como no PDF. */
  id: ParteRaciId;
  nome: string;
}

/** Colunas na ordem do PDF ("Partes (colunas)" da seção 02). */
export const PARTES_RACI: readonly ParteRaci[] = [
  { id: 'EoR', nome: 'Engenheiro de Registro' },
  { id: 'JF', nome: 'JF Brasil' },
  { id: 'RT', nome: 'RT da Barragem' },
  { id: 'LID', nome: 'Liderança Foscentro' },
  { id: 'PAEBM', nome: 'Coordenador PAEBM' },
  { id: 'EOB', nome: 'Equipe Operacional de Barragem' },
  { id: 'PROJ', nome: 'Projetista' },
  { id: 'SMB', nome: 'Sala de Monitoramento de Barragens' },
  { id: 'TTECH', nome: 'TetraTech' },
  { id: 'TECAL', nome: 'TECAL' },
  { id: 'TI', nome: 'TI Foscentro' },
];

export interface AtividadeRaci {
  /** Posição na matriz (1–58), estável enquanto a revisão for a mesma. */
  id: string;
  atividade: string;
  /** Remete à nota ¹ do rodapé. */
  nota?: true;
  /** Letra de cada parte; `null` = célula vazia ("·" no PDF). */
  celulas: Readonly<Record<ParteRaciId, RaciLetra | null>>;
}

export interface SecaoRaci {
  titulo: string;
  atividades: readonly AtividadeRaci[];
}

export const MATRIZ_RACI_AVISO = 'Matriz RACI Rev.1 (Out/2026) — rascunho, aguardando aprovação';

/** "Nota" da legenda do PDF. */
export const MATRIZ_RACI_NOTA_DIAGNOSTICO =
  'O diagnóstico técnico pode ser realizado pelo RT, EoR ou JF Brasil — o RT direciona a demanda. ' +
  'A JF Brasil disponibiliza horas mensais mediante solicitação.';

/** Nota ¹ do rodapé da tabela. */
export const MATRIZ_RACI_NOTA_RODAPE = '¹ Etapas “Diagnóstico” das seções anteriores dos fluxos operacionais.';

/**
 * Legenda com as cores do PDF, adaptadas ao tema escuro do app: R em navy sólido
 * (com contorno claro, para não sumir no fundo), A no amarelo JF, C em azul sem
 * contorno, I em cinza — R e C precisam ser distinguíveis de relance.
 */
export const RACI_LEGENDA: Readonly<Record<RaciLetra, { rotulo: string; fundo: string; texto: string; borda: string }>> = {
  R: { rotulo: 'Responsável pela execução', fundo: '#26314A', texto: '#FFFFFF', borda: '#CBD5E1' },
  A: { rotulo: 'Aprovador / Presta contas', fundo: '#FECB00', texto: '#1A2540', borda: '#FECB00' },
  C: { rotulo: 'Consultado', fundo: 'rgba(120, 180, 207, 0.14)', texto: '#78B4CF', borda: 'transparent' },
  I: { rotulo: 'Informado', fundo: 'transparent', texto: '#94A3B8', borda: 'transparent' },
};

const ORDEM: readonly ParteRaciId[] = PARTES_RACI.map((p) => p.id);

/**
 * Uma linha da matriz. `letras` = 11 símbolos separados por espaço, na ordem das
 * colunas do PDF (EoR JF RT LID PAEBM EOB PROJ SMB TTECH TECAL TI); `·` = vazio.
 * Escrito assim para a conferência contra o PDF ser linha a linha.
 */
type LinhaRaci = Omit<AtividadeRaci, 'id'>;

function linha(atividade: string, letras: string, nota?: true): LinhaRaci {
  const simbolos = letras.split(' ');
  if (simbolos.length !== ORDEM.length) {
    throw new Error(`Matriz RACI: "${atividade}" tem ${simbolos.length} células, esperado ${ORDEM.length}`);
  }
  const celulas = {} as Record<ParteRaciId, RaciLetra | null>;
  ORDEM.forEach((parte, i) => {
    const s = simbolos[i];
    if (s === '·') celulas[parte] = null;
    else if (s === 'R' || s === 'A' || s === 'C' || s === 'I') celulas[parte] = s;
    else throw new Error(`Matriz RACI: célula inválida "${s}" em "${atividade}"`);
  });
  return { atividade, ...(nota ? { nota } : {}), celulas };
}

//                                                       EoR JF RT LID PAEBM EOB PROJ SMB TTECH TECAL TI
const SECOES: readonly { titulo: string; atividades: readonly LinhaRaci[] }[] = [
  {
    titulo: 'Monitoramento e instrumentação',
    atividades: [
      linha('Promover a gestão de mudança entre as equipes do CMG', 'I C C A R · · · C C R'),
      linha('Repetir leitura manual do instrumento', 'I I I A · R · I I · ·'),
      linha('Inserção das leituras manuais no sistema (GPX)', 'I I I A · R · I I · ·'),
      linha('Inserção das leituras manuais no sistema (Geoguard)', 'I R I A · · · I I · ·'),
      linha('Repetir leitura remota do instrumento', 'I I I A · · · I R · ·'),
      linha('Realizar manutenção preventiva e corretiva do instrumento (ou sensor)', 'C I C A · · C I R · ·'),
      linha('Seguir o check list de TARP para cada situação observada', 'C I C A · · · R I · ·'),
    ],
  },
  {
    titulo: 'Câmeras, sirenes e sistema de acionamento automático (AAS)',
    atividades: [
      linha('Realizar acompanhamento das câmeras 24/7', 'I C I A · · · R · C ·'),
      linha(
        'Manter funcional (24/7) e realizar manutenção corretiva e preventiva das câmeras (Imagens com visualização noturna).',
        'I I I A · · · I I R C',
      ),
      linha('Retenção de imagens de vídeo ≥90 dias como linha própria', 'I I I A · · · I I R C'),
      linha(
        'Manter funcional e realizar manutenção corretiva e preventiva das sirenes, seu sistema de acionamento e o sistema automatizado de sirenes pelos tiltímetros',
        'I I I A · · · I C R C',
      ),
    ],
  },
  {
    titulo: 'Níveis de controle e cadastro de instrumentação',
    atividades: [
      linha('Atualização das cartas de risco e níveis de controle', 'C I C A · · R · I I ·'),
      linha('Inserção dos níveis de controle atualizados no sistema (Geoguard)', 'I R I A · · · I I I ·'),
      linha('Registro construtivo e atualização cadastral da instrumentação', 'C I C A · · R · I I ·'),
      linha('Inserção dos dados cadastrais dos instrumentos no sistema (Geoguard)', 'I R I A · · · I I I ·'),
    ],
  },
  {
    titulo: 'TARP — diagnóstico, baixa e plano de ação',
    atividades: [
      linha('Realizar diagnóstico técnico (Etapas “Diagnóstico” das seções anteriores)', 'C C R A · · · I I · ·', true),
      linha('Dar baixa na TARP', 'C C R A · · · I I · ·'),
      linha('Definir plano de ação durante baixa da TARP', 'C C R A · · · I I · ·'),
    ],
  },
  {
    titulo: 'Protocolo PAEBM e comunicação de emergência',
    atividades: [
      linha('Acionamento do protocolo do PAEBM', 'C I C A R I · I · I ·'),
      linha('Coordenar e promover ações previstas o protocolo PAEBM', 'C I C A R I · I · · ·'),
      linha('Acionar Sirene Manualmente', 'I I I · A I I R I I ·'),
      linha('Acionamento e testes do sistema de Sirenes', 'I I I A I I I I I R ·'),
    ],
  },
  {
    titulo: 'Conformidade e monitoramento regulatório',
    atividades: [
      linha('Monitorar EIRs consecutivos / gatilhos de entrada em nível (ANM)', 'C C R A C · C I · · ·'),
      linha('Realizar inspeção rotineira e preenchimento da ficha (FIR). Submissão da EIR no SIGBM.', 'I C R A · I · · · · ·'),
      linha('Realização das análises de estabilidade quinzenais e submissão no SIGBM', 'I C R A · I · · · · ·'),
      linha('Atualização do PSB (Excluída a elaboração de documentos)', 'C I R A R I C · · · ·'),
    ],
  },
  {
    titulo: 'Infraestrutura e operação do CMG',
    atividades: [
      linha(
        'Providenciar sala de monitoramento exclusiva para o monitoramento de barragem no site da Foscentro, incluindo equipamentos de escritório e excluindo PC e telas (fornecidos pela JF Brasil)',
        'I C I A R · · I I · R',
      ),
      linha('Fornecer mão de obra para operação 24/7 do CMG', '· C I A R · · I I I ·'),
      linha('Fornecer o sistema Geoguard', 'I R · A · · · I I I I'),
      linha(
        'Manter funcional e realizar manutenção corretiva e preventiva do sistema Geoguard e fornecer apoio técnico do software',
        '· R · A · · · I · · ·',
      ),
      linha('Dar apoio geotécnico complementar (sobreaviso geotécnico)', 'C R C A · · · I · · ·'),
      linha('Comissionamento dos equipamentos (telas e PC) do CMG', '· R · A · · · · · · C'),
      linha('Fornecer infraestrutura de TI (internet, Energia)', '· C · A · · · · · · R'),
      linha('Fornecer sistema de redundância elétrica e de internet para o CMG', '· C · A · · · · · · R'),
      linha('Fornecer sistema de redundância elétrica e de comunicação para a instrumentação', '· C · A · · · · R · C'),
    ],
  },
  {
    titulo: 'Documentação operacional e PSB',
    atividades: [
      linha('Armazenar dados históricos de instrumentação para consulta dos órgãos fiscalizadores', 'I R C A · · · I R · ·'),
      linha('Atualizar PAEBM quando necessário', 'C I C A R I C I C C ·'),
      linha('Disponibilizar PAEBM físico no CMG', 'I C I A R · · I · · ·'),
      linha(
        'Notificar as partes interessadas (e-mail + sistema) sobre alterações importantes: mudança de nível, alteração cadastral da instrumentação e alteração de níveis de controle',
        'I R I A I · · I I · ·',
      ),
    ],
  },
  {
    titulo: 'Classificação de níveis e gestão de emergência',
    atividades: [
      linha(
        'Classificar e definir níveis de segurança da barragem junto à Equipe Técnica de Segurança conforme Resolução 95 (N-1, N-2, N-3) e, posteriormente, Resolução 220 da ANM',
        'R R R A C I C I I I ·',
      ),
      linha('Declarar o início da situação de emergência no SIGBM', 'C C C A R I · I · · ·'),
      linha(
        'Notificar agentes externos (ANM, Defesa Civil, Prefeituras, Órgãos Ambientais, Corpo de Bombeiros)',
        'I I I A R I · I · · ·',
      ),
      linha('Definir e coordenar a evacuação preventiva/imediata da ZAS junto à Defesa Civil', 'I I I A R I · I · · ·'),
      linha('Acionar o Comitê de Crises da Foscentro', 'I I C A R I · · · · ·'),
      linha(
        'Preencher diariamente as Fichas (FIE) e Extratos (EIE) de Inspeção Especial durante a emergência',
        'C I R A C · · · · · ·',
      ),
      linha('Declarar o encerramento da emergência no SIGBM (até 5 dias)', 'C I C A R I · I · · ·'),
      linha(
        'Contratar/elaborar o Relatório Conclusivo de Inspeção Especial (RCIE) e o RCCA quando aplicável',
        'C C C A R I C · · · ·',
      ),
    ],
  },
  {
    titulo: 'Projetos, estudos e obras',
    atividades: [
      linha(
        'Operação da estrutura (seguir manual de operação, realizar intervenções na estrutura, disposição)',
        'C I C A · R C I I I ·',
      ),
      linha('Elaboração/Atualização do Estudo de Ruptura Hipotética (Dam Break)', 'C I C A C I R I · I ·'),
      linha('Atendimento e respostas a ofícios de órgão fiscalizadores e públicos diversos', 'C I C A C R C · · · ·'),
      linha('Gestão e implementação de recomendações de segurança de barragem nas estruturas', 'C I C A C R C · · · ·'),
      linha('Protocolização de DCE, DCO dos diferentes órgão públicos', 'I I I A · R · · · · ·'),
      linha('Elaboração de documentos de auditoria (RPSB, RISR/RTSB, RCO, etc.)', 'C I C A · C R · · · ·'),
      linha(
        'Elaboração de projetos de diversas naturezas nas barragens (reforço, alteamento, melhoria, etc.)',
        'C I C A · C R · · · ·',
      ),
      linha('Acompanhamento Técnico de Obra e Investigação Geológico-Geotécnica', 'C I C A · C R · · · ·'),
      linha('Contratação e Realização de investigação geológico-geotécnica', 'C I C A · R C I I I ·'),
      linha(
        'Realização de estudos de diversas naturezas (geotécnicos, hidrológicos, hidráulicos, mecânicos, etc.)',
        'C I C A · C R · · · ·',
      ),
      linha(
        'Realizar manutenções corretivas e preventivas nas barragens conforme planejamento (e manual de operação) ou necessidade',
        'C I C A · R C I I I ·',
      ),
    ],
  },
];

/** Matriz com o `id` de cada atividade = posição (1–58) na ordem do PDF. */
export const MATRIZ_RACI: readonly SecaoRaci[] = (() => {
  let n = 0;
  return SECOES.map((s) => ({ ...s, atividades: s.atividades.map((a) => ({ id: String(++n), ...a })) }));
})();

export const TOTAL_ATIVIDADES_RACI = MATRIZ_RACI.reduce((n, s) => n + s.atividades.length, 0);

/**
 * Atividades em que a parte é R ou A (o "filtro por parte" do PDF); seções que
 * ficam vazias saem. `null` = matriz inteira.
 */
export function filtrarMatrizPorParte(
  parte: ParteRaciId | null,
  matriz: readonly SecaoRaci[] = MATRIZ_RACI,
): SecaoRaci[] {
  if (parte === null) return [...matriz];
  return matriz
    .map((s) => ({
      ...s,
      atividades: s.atividades.filter((a) => a.celulas[parte] === 'R' || a.celulas[parte] === 'A'),
    }))
    .filter((s) => s.atividades.length > 0);
}

type PerfilApp = 'operador' | 'tecnico_responsavel' | 'admin';

/**
 * Quem vê a sub-aba: RT e admin. Operador (vigilante) não. Perfil ainda não
 * resolvido (`null`) também não — negar por padrão.
 */
export function podeVerMatrizRaci(perfil: PerfilApp | null): boolean {
  return perfil === 'tecnico_responsavel' || perfil === 'admin';
}
